/**
 * PatientAcuityTrendsDashboard Component
 * Comprehensive dashboard showing patient acuity trends over time
 * Helps identify deteriorating patients for proactive care interventions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, AlertTriangle, TrendingUp, TrendingDown,
  Users, Target, ChevronRight, Loader2, Calendar,
  CheckCircle2, ArrowUp, ArrowDown, Minus,
  BarChart3, LineChart, Brain
} from 'lucide-react';
import PredictiveEscalationAlerts from './PredictiveEscalationAlerts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// StatCard component moved outside to prevent re-creation on render
const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue", trend = null }) => (
  <Card className="border">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {trend !== null && (
              <span className={`text-xs font-medium ${trend > 0 ? 'text-red-500' : trend < 0 ? 'text-emerald-500' : 'text-gray-400'}`}>
                {trend > 0 ? `+${trend}` : trend}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PatientAcuityTrendsDashboard = ({ className = "" }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState(14);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('hospice_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/scheduling/acuity-dashboard?days=${selectedTimeRange}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        toast.error('Failed to load acuity dashboard');
      }
    } catch (error) {
      console.error('Error fetching acuity dashboard:', error);
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const getAcuityColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'moderate': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getAcuityBgColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'moderate': return 'bg-amber-500';
      case 'low': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'declining': return <ArrowUp className="w-4 h-4 text-red-500" />;
      case 'improving': return <ArrowDown className="w-4 h-4 text-emerald-500" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <Card className={className} data-testid="acuity-dashboard-loading">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            <span className="text-slate-600">Loading acuity trends...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = dashboardData?.summary || {};
  const distribution = dashboardData?.distribution || {};
  const trends = dashboardData?.trends || [];
  const decliningPatients = dashboardData?.declining_patients || [];
  const improvingPatients = dashboardData?.improving_patients || [];
  const alerts = dashboardData?.alerts || [];
  const allPatients = dashboardData?.all_patients || [];

  return (
    <div className={className} data-testid="patient-acuity-trends-dashboard">
      {/* Header */}
      <Card className="mb-6 border-2 border-indigo-200/50 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <LineChart className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Patient Acuity Trends Dashboard</CardTitle>
                <CardDescription>
                  Monitor patient condition changes and identify those needing proactive intervention
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(Number(e.target.value))}
                className="px-3 py-2 text-sm border rounded-lg bg-white"
                data-testid="time-range-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchDashboard} data-testid="refresh-acuity-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`p-3 rounded-lg flex items-center gap-3 ${
                alert.type === 'critical' ? 'bg-red-50 border border-red-200' :
                alert.type === 'declining' ? 'bg-amber-50 border border-amber-200' :
                'bg-blue-50 border border-blue-200'
              }`}
              data-testid={`alert-${alert.type}`}
            >
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                alert.type === 'critical' ? 'text-red-500' :
                alert.type === 'declining' ? 'text-amber-500' :
                'text-blue-500'
              }`} />
              <span className="text-sm font-medium flex-1">{alert.message}</span>
              <Badge variant="outline" className="bg-white">
                {alert.patient_count} patient(s)
              </Badge>
            </div>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="declining" className="gap-2">
            <TrendingUp className="w-4 h-4 text-red-500" />
            Declining
            {decliningPatients.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {decliningPatients.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="improving" className="gap-2">
            <TrendingDown className="w-4 h-4 text-emerald-500" />
            Improving
          </TabsTrigger>
          <TabsTrigger value="all-patients" className="gap-2">
            <Users className="w-4 h-4" />
            All Patients
          </TabsTrigger>
          <TabsTrigger value="predictive" className="gap-2">
            <Brain className="w-4 h-4 text-purple-500" />
            AI Predictions
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard title="Total Patients" value={summary.total_patients || 0} icon={Users} color="blue" />
            <StatCard title="Critical" value={summary.critical_count || 0} icon={AlertTriangle} color="red" subtitle="Immediate attention" />
            <StatCard title="High Acuity" value={summary.high_count || 0} icon={Activity} color="orange" />
            <StatCard title="Avg Score" value={summary.avg_acuity_score || 0} icon={Target} color="purple" />
            <StatCard title="Declining" value={summary.declining_count || 0} icon={TrendingUp} color="red" subtitle="Needs monitoring" />
          </div>

          {/* Distribution & Trend Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Acuity Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-500" />
                  Acuity Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { level: 'critical', label: 'Critical', color: 'bg-red-500', count: distribution.critical || 0 },
                    { level: 'high', label: 'High', color: 'bg-orange-500', count: distribution.high || 0 },
                    { level: 'moderate', label: 'Moderate', color: 'bg-amber-500', count: distribution.moderate || 0 },
                    { level: 'low', label: 'Low', color: 'bg-emerald-500', count: distribution.low || 0 }
                  ].map((item) => {
                    const percentage = summary.total_patients 
                      ? Math.round((item.count / summary.total_patients) * 100) 
                      : 0;
                    return (
                      <div key={item.level} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{item.label}</span>
                          <span className="text-slate-500">{item.count} ({percentage}%)</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${item.color} rounded-full transition-all duration-500`} 
                            style={{ width: `${percentage}%` }} 
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Trend Status Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-indigo-500" />
                  Patient Trends (7-Day)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-red-50 rounded-xl border border-red-100">
                    <TrendingUp className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-700">{summary.declining_count || 0}</p>
                    <p className="text-xs text-red-600 font-medium">Declining</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <Minus className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-700">{summary.stable_count || 0}</p>
                    <p className="text-xs text-gray-600 font-medium">Stable</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <TrendingDown className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-700">{summary.improving_count || 0}</p>
                    <p className="text-xs text-emerald-600 font-medium">Improving</p>
                  </div>
                </div>
                
                {/* Mini Trend Chart Representation */}
                {trends.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-slate-500 mb-2">Average Acuity Trend</p>
                    <div className="flex items-end justify-between h-16 gap-1">
                      {trends.slice(-14).map((day, idx) => (
                        <div
                          key={idx}
                          className="flex-1 bg-indigo-500 rounded-t transition-all hover:bg-indigo-600"
                          style={{ height: `${Math.max(10, (day.avg_acuity / 100) * 100)}%` }}
                          title={`${day.date}: ${day.avg_acuity}`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>{trends[0]?.date?.slice(5)}</span>
                      <span>{trends[trends.length - 1]?.date?.slice(5)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick View of Declining Patients */}
          {decliningPatients.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Patients Requiring Attention
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setActiveTab('declining')}
                    className="text-red-600 hover:text-red-700"
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {decliningPatients.slice(0, 3).map((patient, idx) => (
                    <div
                      key={patient.patient_id || idx}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100"
                      data-testid={`declining-patient-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <ArrowUp className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{patient.patient_name}</p>
                          <p className="text-xs text-slate-500">{patient.primary_diagnosis}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">{patient.current_score}</p>
                          <p className="text-xs text-red-500">+{patient.change} from last week</p>
                        </div>
                        <Badge className={getAcuityColor(patient.acuity_level)}>
                          {patient.acuity_level?.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Declining Patients Tab */}
        <TabsContent value="declining" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <TrendingUp className="w-5 h-5" />
                Patients with Declining Acuity
              </CardTitle>
              <CardDescription>
                These patients show increasing acuity scores, indicating potential deterioration
              </CardDescription>
            </CardHeader>
            <CardContent>
              {decliningPatients.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="font-medium">No declining patients detected</p>
                  <p className="text-sm">All patients are stable or improving</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {decliningPatients.map((patient, idx) => (
                    <div
                      key={patient.patient_id || idx}
                      className="p-4 bg-white border rounded-lg hover:shadow-md transition-shadow"
                      data-testid={`declining-detail-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <ArrowUp className="w-6 h-6 text-red-500" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{patient.patient_name}</p>
                            <p className="text-sm text-slate-500">{patient.primary_diagnosis}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                PPS: {patient.pps}%
                              </span>
                              {patient.days_since_visit && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {patient.days_since_visit}d since visit
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-400">{patient.previous_score}</span>
                            <ChevronRight className="w-4 h-4 text-red-400" />
                            <span className="text-2xl font-bold text-red-600">{patient.current_score}</span>
                          </div>
                          <Badge className={`mt-1 ${getAcuityColor(patient.acuity_level)}`}>
                            {patient.acuity_level?.toUpperCase()}
                          </Badge>
                          <p className="text-xs text-red-500 mt-1">+{patient.change} points</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Improving Patients Tab */}
        <TabsContent value="improving" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <TrendingDown className="w-5 h-5" />
                Patients with Improving Acuity
              </CardTitle>
              <CardDescription>
                These patients show decreasing acuity scores, indicating positive response to care
              </CardDescription>
            </CardHeader>
            <CardContent>
              {improvingPatients.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No improving patients detected</p>
                  <p className="text-sm">Patients are stable or need attention</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {improvingPatients.map((patient, idx) => (
                    <div
                      key={patient.patient_id || idx}
                      className="p-4 bg-white border border-emerald-100 rounded-lg hover:shadow-md transition-shadow"
                      data-testid={`improving-detail-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <ArrowDown className="w-6 h-6 text-emerald-500" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{patient.patient_name}</p>
                            <p className="text-sm text-slate-500">{patient.primary_diagnosis}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                PPS: {patient.pps}%
                              </span>
                              {patient.days_since_visit && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {patient.days_since_visit}d since visit
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-400">{patient.previous_score}</span>
                            <ChevronRight className="w-4 h-4 text-emerald-400" />
                            <span className="text-2xl font-bold text-emerald-600">{patient.current_score}</span>
                          </div>
                          <Badge className={`mt-1 ${getAcuityColor(patient.acuity_level)}`}>
                            {patient.acuity_level?.toUpperCase()}
                          </Badge>
                          <p className="text-xs text-emerald-500 mt-1">{patient.change} points</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Patients Tab */}
        <TabsContent value="all-patients" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                All Patient Acuity Scores
              </CardTitle>
              <CardDescription>
                Complete list of patients sorted by acuity score (highest first)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-slate-500">
                      <th className="pb-3 font-medium">Patient</th>
                      <th className="pb-3 font-medium">Diagnosis</th>
                      <th className="pb-3 font-medium text-center">PPS</th>
                      <th className="pb-3 font-medium text-center">Score</th>
                      <th className="pb-3 font-medium text-center">Trend</th>
                      <th className="pb-3 font-medium text-center">Level</th>
                      <th className="pb-3 font-medium text-center">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPatients.slice(0, 20).map((patient, idx) => (
                      <tr key={patient.patient_id || idx} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-3 font-medium text-slate-900">{patient.patient_name}</td>
                        <td className="py-3 text-sm text-slate-500">{patient.primary_diagnosis}</td>
                        <td className="py-3 text-center text-sm">{patient.pps}%</td>
                        <td className="py-3 text-center font-bold">{patient.current_score}</td>
                        <td className="py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getTrendIcon(patient.trend)}
                            <span className={`text-xs ${
                              patient.trend === 'declining' ? 'text-red-500' :
                              patient.trend === 'improving' ? 'text-emerald-500' : 'text-gray-400'
                            }`}>
                              {patient.change > 0 ? `+${patient.change}` : patient.change}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 text-center">
                          <Badge className={`text-xs ${getAcuityColor(patient.acuity_level)}`}>
                            {patient.acuity_level?.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 text-center text-sm text-slate-500">
                          {patient.days_since_visit ? `${patient.days_since_visit}d ago` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Predictive AI Tab */}
        <TabsContent value="predictive" className="space-y-4">
          <PredictiveEscalationAlerts />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PatientAcuityTrendsDashboard;
